/**
 * Bundle offer nudge in the cart.
 *
 * Reads the cart (cart.js), counts the pieces that qualify for the bundle (price at or
 * above the minimum, not the packaging add-on, the exchange fee or an exchange line),
 * finds the next tier of the price ladder and says what it costs next to what the
 * customer pays for those pieces right now. A quick-add row under the message adds
 * products without leaving the drawer.
 *
 * - Essential Cart drawer (#essential-cart-drawer-portal): inserted above or below the
 *   line items (Theme settings › Cart › Bundle offer nudge › Position).
 * - Theme cart page: any element with [data-jq-nudge-slot] receives the same card.
 * Configuration comes from snippets/jewelique-bundle-nudge.liquid.
 */
(() => {
  if (customElements.get('jewelique-bundle-nudge')) return;

  const PORTAL = '#essential-cart-drawer-portal';
  const LIST_WRAPPER = '[data-essential-cart-element="cart-drawer-item-list-wrapper"]';

  class JeweliqueBundleNudge extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.root = (this.dataset.root || '/').replace(/\/?$/, '/');
      this.template = this.querySelector('template[data-jq-nudge-card]');
      this.currency = this.dataset.currency || '';
      // cart.js reports every amount in hundredths, whatever the currency's own precision
      // (14.900 KWD arrives as 1490).
      this.unit = 100;
      this.minCents = Math.round(parseFloat(this.dataset.minPrice || '0') * this.unit) || 0;
      this.exclude = (this.dataset.exclude || '').split(',').map((h) => h.trim().toLowerCase()).filter(Boolean);
      this.ladder = (this.dataset.ladder || '')
        .split(',')
        .map((pair) => pair.split(':'))
        .map(([qty, price]) => ({ qty: parseInt(qty, 10), cents: Math.round(parseFloat(price) * this.unit) }))
        .filter((t) => t.qty > 0 && isFinite(t.cents))
        .sort((a, b) => a.qty - b.qty);
      if (!this.template || !this.ladder.length) return;
      this.cart = null;
      this.busy = false;

      document.addEventListener('click', (event) => {
        const close = event.target.closest?.('.jq-nudge__close');
        if (close) {
          event.preventDefault();
          event.stopPropagation();
          this.dismiss();
          return;
        }
        const button = event.target.closest?.('.jq-nudge__add');
        if (!button) return;
        event.preventDefault();
        event.stopPropagation();
        this.add(button);
      }, true);
      document.addEventListener('jewelique:cart', (event) => {
        if (event.detail) this.adopt(event.detail, false);
      });
      this.interceptFetch();

      const discover = () => {
        const portal = document.querySelector(PORTAL);
        if (!portal) return false;
        this.portal = portal;
        new MutationObserver((records) => {
          if (records.some((r) => !r.target.closest?.('.jq-nudge-slot'))) this.schedule();
        }).observe(portal, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'open', 'class'] });
        this.schedule();
        return true;
      };
      if (!discover()) {
        const observer = new MutationObserver(() => {
          if (discover()) observer.disconnect();
        });
        observer.observe(document.body, { childList: true, subtree: true });
        setTimeout(() => observer.disconnect(), 15000);
      }
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.refresh(), 150);
    }

    async refresh() {
      if (this.cart && Date.now() - this.cartAt < 1500) return this.render();
      try {
        const response = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (response.ok) this.adopt(await response.json(), false);
      } catch (error) {
        /* keep the last cart */
      }
    }

    adopt(cart, announce) {
      if (!cart || !Array.isArray(cart.items)) return;
      this.cart = cart;
      this.cartAt = Date.now();
      this.render();
      if (announce) document.dispatchEvent(new CustomEvent('jewelique:cart', { detail: cart }));
    }

    interceptFetch() {
      if (window.__jqNudgeFetchPatched || typeof window.fetch !== 'function') return;
      window.__jqNudgeFetchPatched = true;
      const original = window.fetch;
      const cartCall = /\/cart(?:\/(?:add|change|update|clear))?\.js(?:[?#]|$)/;
      window.fetch = (...args) => {
        const promise = original.apply(window, args);
        try {
          const input = args[0];
          const url = typeof input === 'string' ? input : input?.url || '';
          if (cartCall.test(url)) promise.then(() => this.schedule()).catch(() => {});
        } catch (error) {
          /* never interfere with the original call */
        }
        return promise;
      };
    }

    /** Pieces that count for the bundle and what they cost right now (after discounts). */
    eligible() {
      let count = 0;
      let excluded = 0;
      const productIds = new Set();
      for (const item of this.cart?.items || []) {
        const skip =
          this.exclude.includes(String(item.handle || '').toLowerCase()) ||
          (item.properties && item.properties._exchange) ||
          (item.original_price || 0) < this.minCents;
        if (skip) {
          excluded += item.final_line_price || 0;
          continue;
        }
        count += item.quantity;
        productIds.add(item.product_id);
      }
      // The cart total is exact; per-line prices are rounded (a set can read 9.99 next to 19.90).
      const current = Math.max((this.cart?.total_price || 0) - excluded, 0);
      return { count, current, productIds };
    }

    money(cents) {
      const format = this.dataset.moneyFormat || '{{amount}}';
      // Same rules as Shopify's money filter: {{amount}} always carries two decimals (the
      // Kuwait store's format appends a third digit itself), so nothing is trimmed here.
      const [whole, fraction] = (cents / this.unit).toFixed(2).split('.');
      const group = (s, sep) => s.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
      let text;
      if (/amount_no_decimals_with_comma_separator/.test(format)) text = group(whole, '.');
      else if (/amount_no_decimals_with_space_separator/.test(format)) text = group(whole, ' ');
      else if (/amount_no_decimals/.test(format)) text = whole;
      else if (/amount_with_comma_separator/.test(format)) text = group(whole, '.') + ',' + fraction;
      else if (/amount_with_space_separator/.test(format)) text = group(whole, ' ') + ',' + fraction;
      else if (/amount_with_apostrophe_separator/.test(format)) text = group(whole, "'") + '.' + fraction;
      else text = group(whole, ',') + '.' + fraction;
      return format.replace(/\{\{\s*amount[a-z_]*\s*\}\}/g, text).replace(/<[^>]+>/g, '').trim();
    }

    fill(template, values) {
      return String(template || '').replace(/\{(count|price|current|save)\}/g, (m, key) => values[key] ?? m);
    }

    /** The x hides the card until the cart moves to a different tier (remembered for the session). */
    dismiss() {
      const msg = this.message();
      this.dismissedKey = msg ? msg.key : '';
      try {
        sessionStorage.setItem('jq-nudge-dismissed', this.dismissedKey);
      } catch (error) {
        /* private mode: remembered for this page only */
      }
      this.render();
    }

    dismissed(key) {
      if (this.dismissedKey === undefined) {
        try {
          this.dismissedKey = sessionStorage.getItem('jq-nudge-dismissed') || '';
        } catch (error) {
          this.dismissedKey = '';
        }
      }
      return this.dismissedKey === key;
    }

    /** The message for the cart as it is, or null when nothing should show. */
    message() {
      const { count, current } = this.eligible();
      if (!count) return null;
      const next = this.ladder.find((t) => t.qty > count);
      if (!next) return null;
      const missing = next.qty - count;
      // Only when a single piece is missing, and only up to the configured tier (6 pieces by default).
      if (this.dataset.onlyOneAway !== 'false' && missing !== 1) return null;
      const maxTier = parseInt(this.dataset.maxTier || '0', 10);
      if (maxTier > 0 && next.qty > maxTier) return null;
      const save = current - next.cents;
      const values = {
        count: String(missing),
        price: this.money(next.cents),
        current: this.money(current),
        save: this.money(Math.max(save, 0)),
      };
      const d = this.dataset;
      const key = `tier-${next.qty}`;
      if (save > 0) {
        const template = missing === 1 && d.textGapOne ? d.textGapOne : d.textGapMany || d.textGapOne;
        return { text: this.fill(template, values), key };
      }
      const template = missing === 1 && d.textMoreOne ? d.textMoreOne : d.textMoreMany || d.textMoreOne;
      return { text: this.fill(template, values), key };
    }

    card() {
      return this.template.content.firstElementChild.cloneNode(true);
    }

    update(node, msg) {
      node.querySelector('.jq-nudge__message').textContent = msg.text;
      const { productIds } = this.eligible();
      node.querySelectorAll('.jq-nudge__item').forEach((item) => {
        item.hidden = productIds.has(Number(item.dataset.productId));
      });
      const row = node.querySelector('.jq-nudge__row');
      if (row) row.hidden = ![...row.children].some((item) => !item.hidden);
    }

    place(slot) {
      const wrapper = this.portal.querySelector(LIST_WRAPPER);
      if (!wrapper) return false;
      const above = this.dataset.position !== 'below';
      const wanted = above ? wrapper.previousElementSibling === slot : wrapper.nextElementSibling === slot;
      if (!wanted) (above ? wrapper.before(slot) : wrapper.after(slot));
      return true;
    }

    render() {
      if (!this.cart) return;
      let msg = this.message();
      if (msg && this.dismissed(msg.key)) msg = null;

      if (this.portal) {
        let slot = this.portal.querySelector('.jq-nudge-slot');
        if (msg) {
          if (!slot) {
            slot = document.createElement('div');
            slot.className = 'jq-nudge-slot';
            slot.append(this.card());
          }
          if (this.place(slot)) this.update(slot.firstElementChild, msg);
        } else if (slot) {
          slot.remove();
        }
      }

      document.querySelectorAll('[data-jq-nudge-slot]').forEach((slot) => {
        if (this.portal && this.portal.contains(slot)) return;
        if (!msg) {
          slot.innerHTML = '';
          return;
        }
        if (!slot.querySelector('.jq-nudge')) slot.append(this.card());
        this.update(slot.querySelector('.jq-nudge'), msg);
      });
    }

    async add(button) {
      if (this.busy) return;
      const item = button.closest('.jq-nudge__item');
      const select = item?.querySelector('.jq-nudge__variant');
      const variant = Number(button.hasAttribute('data-from-select') && select ? select.value : button.dataset.variantId);
      if (!variant) return;
      this.busy = true;
      button.classList.add('jq-nudge__add--busy');
      try {
        const response = await fetch(`${this.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variant, quantity: 1 }] }),
        });
        if (!response.ok) throw new Error(`cart/add.js ${response.status}`);
        const cart = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        this.adopt(await cart.json(), true);
        try {
          window.essentialCartApi?.refreshCart?.();
        } catch (error) {
          /* the drawer also watches cart calls itself */
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'jewelique-bundle-nudge', cartData: this.cart });
        }
        this.refreshCartPage();
      } catch (error) {
        if (console && console.warn) console.warn('[jewelique-bundle-nudge]', error);
      } finally {
        this.busy = false;
        button.classList.remove('jq-nudge__add--busy');
        this.schedule();
      }
    }

    /** Theme cart page: redraw Dawn's cart sections the way cart.js does after a quantity change. */
    async refreshCartPage() {
      const sections = [
        { id: 'main-cart-items', selector: '.js-contents' },
        { id: 'main-cart-footer', selector: '.js-contents' },
        { id: 'cart-icon-bubble', selector: '.shopify-section' },
      ]
        .map((section) => ({ ...section, element: document.getElementById(section.id) }))
        .filter((section) => section.element)
        .map((section) => ({ ...section, key: section.element.dataset.id || section.id }));
      if (!sections.some((section) => section.id === 'main-cart-items')) return;
      try {
        const response = await fetch(`${this.root}cart?sections=${sections.map((s) => s.key).join(',')}`, { cache: 'no-store' });
        if (!response.ok) return;
        const html = await response.json();
        sections.forEach((section) => {
          const target = section.element.querySelector(section.selector) || section.element;
          const source = new DOMParser().parseFromString(html[section.key] || '', 'text/html').querySelector(section.selector);
          if (source) target.innerHTML = source.innerHTML;
        });
        this.schedule();
      } catch (error) {
        /* the cart itself is already updated */
      }
    }
  }

  customElements.define('jewelique-bundle-nudge', JeweliqueBundleNudge);
})();
